import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: ModalProps) {
  return <AnimatePresence data-id="element-41">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={onClose} data-id="element-42" />
          <motion.div initial={{
        opacity: 0,
        y: 40,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 40,
        scale: 0.97
      }} transition={{
        duration: 0.3,
        ease: [0.21, 0.47, 0.32, 0.98]
      }} className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[80vh] bg-cream z-50 shadow-2xl flex flex-col" data-id="element-43">
            <div className="flex items-center justify-between p-6 border-b border-copper/20 flex-shrink-0" data-id="element-44">
              <h2 className="text-2xl font-heading font-bold text-navy" data-id="element-45">
                {title}
              </h2>
              <button onClick={onClose} className="w-10 h-10 flex items-center justify-center text-navy/60 hover:text-navy hover:bg-navy/10 rounded-full transition-colors" aria-label="Close" data-id="element-46">
                <XIcon className="w-5 h-5" data-id="element-47" />
              </button>
            </div>
            <div className="p-6 md:p-8 overflow-y-auto font-body text-text-dark/80 leading-relaxed text-sm md:text-base space-y-4" data-id="element-48">
              {children}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function Footer() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  return <>
      <footer id="page-bottom" className="bg-navy-dark py-12 px-4 border-t border-copper/10 text-center" data-id="element-49">
        <div className="max-w-4xl mx-auto" data-id="element-50">
          <p className="text-cream font-heading text-sm tracking-widest uppercase" data-id="element-51">
            © {new Date().getFullYear()} Diloré Publishing. All rights
            reserved.
          </p>

          <div className="flex items-center justify-center gap-6 mt-5" data-id="element-52">
            <button onClick={() => setPrivacyOpen(true)} className="text-white font-body text-sm hover:text-copper transition-colors cursor-pointer" data-id="element-53">
              Privacy Policy
            </button>
            <span className="text-white/60" data-id="element-54">|</span>
            <button onClick={() => setTermsOpen(true)} className="text-white font-body text-sm hover:text-copper transition-colors cursor-pointer" data-id="element-55">
              Terms of Service
            </button>
          </div>

          <p className="text-white/80 font-body text-xs mt-5 max-w-2xl mx-auto leading-relaxed" data-id="element-56">
            This material is for informational purposes only and does not
            constitute legal or financial advice. Consult with a qualified
            professional regarding your specific situation.
          </p>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <LegalModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Privacy Policy" data-id="element-57">
        <p className="font-semibold text-navy" data-id="element-58">
          Effective Date: March 18, 2026
        </p>
        <p data-id="element-59">
          Diloré Publishing ("we," "us," or "our") respects your privacy and is
          committed to protecting the personal information you share with us.
          This Privacy Policy explains how we collect, use, and safeguard your
          information when you purchase or use The Inherited House Survival Kit.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-60">
          Information We Collect
        </h3>
        <p data-id="element-61">
          When you make a purchase, we collect only the information necessary to
          process your transaction, including your name, email address, and
          payment details. Payment information is processed securely through our
          third-party payment processor and is never stored on our servers.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-62">
          How We Use Your Information
        </h3>
        <p data-id="element-63">We use your information to:</p>
        <ul className="list-disc pl-6 space-y-1" data-id="element-64">
          <li data-id="element-65">Deliver your purchased digital product</li>
          <li data-id="element-66">Send purchase confirmation and download links</li>
          <li data-id="element-67">Respond to customer service inquiries</li>
          <li data-id="element-68">Comply with legal obligations</li>
        </ul>
        <p data-id="element-69">
          We do not sell, rent, or share your personal information with third
          parties for marketing purposes.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-70">
          Cookies & Tracking
        </h3>
        <p data-id="element-71">
          Our website may use essential cookies to ensure proper functionality.
          We do not use tracking cookies for advertising purposes.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-72">
          Data Security
        </h3>
        <p data-id="element-73">
          We implement industry-standard security measures to protect your
          personal information. However, no method of electronic transmission or
          storage is 100% secure, and we cannot guarantee absolute security.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-74">
          Your Rights
        </h3>
        <p data-id="element-75">
          You have the right to request access to, correction of, or deletion of
          your personal data. To exercise these rights, please reach out to us
          through the channels provided at the time of purchase.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-76">
          Changes to This Policy
        </h3>
        <p data-id="element-77">
          We may update this Privacy Policy from time to time. Any changes will
          be posted on this page with an updated effective date.
        </p>
      </LegalModal>

      {/* Terms of Service Modal */}
      <LegalModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Terms of Service" data-id="element-78">
        <p className="font-semibold text-navy" data-id="element-79">
          Effective Date: March 18, 2026
        </p>
        <p data-id="element-80">
          By purchasing and using The Inherited House Survival Kit ("the
          Product"), you agree to the following terms and conditions set forth
          by Diloré Publishing ("we," "us," or "our").
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-81">
          Product Description
        </h3>
        <p data-id="element-82">
          The Inherited House Survival Kit is a digital informational product
          delivered as a downloadable PDF and checklist bundle. It is designed
          to provide general guidance for individuals navigating inherited
          property situations.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-83">
          Disclaimer
        </h3>
        <p data-id="element-84">
          The content provided in this product is for informational and
          educational purposes only. It does not constitute legal, financial,
          tax, or real estate advice. We strongly recommend consulting with
          qualified professionals (attorneys, CPAs, licensed real estate agents)
          regarding your specific situation. Results may vary based on
          individual circumstances, state laws, and other factors.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-85">
          Digital Product Delivery
        </h3>
        <p data-id="element-86">
          Upon successful payment, you will receive immediate access to download
          the Product. Due to the digital nature of this product, all sales are
          final. We do not offer refunds once the download link has been
          delivered.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-87">
          Intellectual Property
        </h3>
        <p data-id="element-88">
          All content within The Inherited House Survival Kit is the
          intellectual property of Diloré Publishing. You are granted a
          personal, non-transferable license to use the Product for your own
          purposes. You may not reproduce, distribute, resell, or share the
          Product or any portion of it without prior written consent.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-89">
          Limitation of Liability
        </h3>
        <p data-id="element-90">
          To the fullest extent permitted by law, Diloré Publishing shall not be
          liable for any direct, indirect, incidental, consequential, or special
          damages arising from your use of or inability to use the Product. Your
          use of the information provided is at your own risk.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-91">
          Governing Law
        </h3>
        <p data-id="element-92">
          These Terms of Service shall be governed by and construed in
          accordance with the laws of the United States, without regard to
          conflict of law principles.
        </p>

        <h3 className="font-heading font-bold text-navy text-lg mt-6" data-id="element-93">
          Changes to These Terms
        </h3>
        <p data-id="element-94">
          We reserve the right to modify these Terms of Service at any time.
          Updated terms will be posted on this page with a revised effective
          date. Continued use of the Product constitutes acceptance of the
          updated terms.
        </p>
      </LegalModal>
    </>;
}